import React, { FunctionComponent, useContext } from 'react';
import { AppDeviceSize } from '../../enums/app-device-size';
import { AppStore } from '../../store/app.context';
import { countRows, checkLastItemInView, checkIsEven } from './utils';
import styles from './arch-line-section.module.scss';
import {
  ImgTitleDescriptionComponent,
  IImgTitleDescriptionComponentProps
} from '../img-title-description/img-title-description';
import { Fade } from 'react-awesome-reveal';

export interface IArchLineSectionProps {
  children: IImgTitleDescriptionComponentProps[];
}

export const ArchLineSection: FunctionComponent<IArchLineSectionProps> = ({
  children
}) => {
  const { state } = useContext(AppStore);
  const { showActiveDevice } = state;
  const rows = countRows(children.length);
  const isEven = checkIsEven(rows);
  const lasItemInView = checkLastItemInView(isEven, children.length);

  // Count how many arches to render
  const countRightArches = (): number =>
    isEven ? rows / 2 : Math.floor(rows / 2);
  const countLeftArches = (): number =>
    isEven ? Math.floor(rows / 3) : Math.ceil(rows / 3);

  // Create right arches elements and add grid row position
  const mapRightArches = (): JSX.Element[] => {
    const rightArches = [];
    const arches = countRightArches();

    for (let i = 1; i <= arches; i++) {
      const gridRow = i === 1 ? 1 : i + (i - 1);

      rightArches.push(
        <div
          style={{ gridRow: `${gridRow} / ${gridRow}` }}
          className={styles.dottedArchWrapperRight}
          key={i}
        >
          <div className={styles.dottedArchRight}></div>
        </div>
      );
    }
    return rightArches;
  };

  // Create left arches elements and add grid row position
  const mapLeftArches = (): JSX.Element[] => {
    const leftArches = [];
    const arches = countLeftArches();

    for (let i = 1; i <= arches; i++) {
      const gridRow = i === 1 ? 2 : i + i;

      leftArches.push(
        <div
          style={{ gridRow: `${gridRow} / ${gridRow}` }}
          className={styles.dottedArchWrapperLeft}
          key={i}
        >
          <div className={styles.dottedArchLeft}></div>
        </div>
      );
    }
    return leftArches;
  };

  const mobileView = (
    <div className={styles.itemsWrapper}>
      {children &&
        children.map((item: IImgTitleDescriptionComponentProps, i) => (
          <div
            className={`${styles.item} 
          ${i === lasItemInView && styles.itemLast}`}
            data-item={i}
            key={i}
          >
            <ImgTitleDescriptionComponent {...item} />
          </div>
        ))}
    </div>
  );

  return (
    <div className={styles.container} data-items={children.length}>
      {showActiveDevice >= AppDeviceSize.SMALLDESKTOP ? (
        <Fade
          className={styles.itemsWrapper}
          cascade
          duration={300}
          triggerOnce
        >
          {children &&
            children.map((item: IImgTitleDescriptionComponentProps, i) => (
              <div
                className={`${styles.item} 
                  ${i === lasItemInView && styles.itemLast}`}
                data-item-index={i}
                key={i}
              >
                <ImgTitleDescriptionComponent {...item} />
              </div>
            ))}
          {mapRightArches()}
          {mapLeftArches()}
        </Fade>
      ) : (
        mobileView
      )}
    </div>
  );
};
